/**
 * Pricing Admin Dashboard Component
 * Comprehensive pricing management interface for admins
 */

import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Save, Eye, X } from "lucide-react";
import PricingPage from "../../pages/pricing";
import { API_ROOT } from "../../utils/apiConfig";

const PricingAdminDashboard = ({ isDarkTheme = false, token = null }) => {
  const [activeTab, setActiveTab] = useState("plans");
  const [pricingPlans, setPricingPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [showNewFAQForm, setShowNewFAQForm] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const adminToken = token || localStorage.getItem("adminToken"); // Accept token as prop or fallback to localStorage

  // ─── FETCH DATA ───

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ROOT}/pricing/admin/plans`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setPricingPlans(data.data);
      }
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ROOT}/pricing/admin/faqs`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setFaqs(data.data);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ROOT}/pricing/admin/settings`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "plans") {
      fetchPricingPlans();
    } else if (activeTab === "faqs") {
      fetchFAQs();
    } else if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab]);

  // ─── PRICING PLANS ───

  const handleCreatePlan = async (planData) => {
    try {
      const response = await fetch(`${API_ROOT}/pricing/admin/plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(planData),
      });
      const data = await response.json();
      if (data.success) {
        setPricingPlans([...pricingPlans, data.data]);
        setShowNewPlanForm(false);
        alert("Pricing plan created successfully!");
      } else {
        alert("Error: " + (data.errors?.join(", ") || data.error));
      }
    } catch (error) {
      alert("Error creating plan: " + error.message);
    }
  };

  const handleUpdatePlan = async (planId, planData) => {
    try {
      const response = await fetch(
        `${API_ROOT}/pricing/admin/plans/${planId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify(planData),
        },
      );
      const data = await response.json();
      if (data.success) {
        setPricingPlans(
          pricingPlans.map((p) => (p._id === planId ? data.data : p)),
        );
        setEditingPlan(null);
        alert("Pricing plan updated successfully!");
      } else {
        alert("Error: " + (data.errors?.join(", ") || data.error));
      }
    } catch (error) {
      alert("Error updating plan: " + error.message);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;

    try {
      const response = await fetch(
        `${API_ROOT}/pricing/admin/plans/${planId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${adminToken}` },
        },
      );
      const data = await response.json();
      if (data.success) {
        setPricingPlans(pricingPlans.filter((p) => p._id !== planId));
        alert("Pricing plan deleted successfully!");
      }
    } catch (error) {
      alert("Error deleting plan: " + error.message);
    }
  };

  // ─── FAQs ───

  const handleCreateFAQ = async (faqData) => {
    try {
      const response = await fetch(`${API_ROOT}/pricing/admin/faqs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(faqData),
      });
      const data = await response.json();
      if (data.success) {
        setFaqs([...faqs, data.data]);
        setShowNewFAQForm(false);
        alert("FAQ created successfully!");
      } else {
        alert("Error: " + (data.errors?.join(", ") || data.error));
      }
    } catch (error) {
      alert("Error creating FAQ: " + error.message);
    }
  };

  const handleUpdateFAQ = async (faqId, faqData) => {
    try {
      const response = await fetch(`${API_ROOT}/pricing/admin/faqs/${faqId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(faqData),
      });
      const data = await response.json();
      if (data.success) {
        setFaqs(faqs.map((f) => (f._id === faqId ? data.data : f)));
        setEditingFAQ(null);
        alert("FAQ updated successfully!");
      } else {
        alert("Error: " + (data.errors?.join(", ") || data.error));
      }
    } catch (error) {
      alert("Error updating FAQ: " + error.message);
    }
  };

  const handleDeleteFAQ = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      const response = await fetch(`${API_ROOT}/pricing/admin/faqs/${faqId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setFaqs(faqs.filter((f) => f._id !== faqId));
        alert("FAQ deleted successfully!");
      }
    } catch (error) {
      alert("Error deleting FAQ: " + error.message);
    }
  };

  // ─── SETTINGS ───

  const handleUpdateSettings = async (nextSettings) => {
    try {
      const response = await fetch(`${API_ROOT}/pricing/admin/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(nextSettings),
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
        alert("Settings updated successfully!");
      } else {
        alert("Error: " + (data.errors?.join(", ") || data.error));
      }
    } catch (error) {
      alert("Error updating settings: " + error.message);
    }
  };

  // ─── RENDER COMPONENTS ───

  const showPreview = ({
    plans = pricingPlans,
    nextFaqs = faqs,
    nextSettings = settings,
  } = {}) => {
    setPreviewData({
      pricingPlans: plans,
      faqs: nextFaqs,
      settings: nextSettings,
    });
    setPreviewOpen(true);
  };

  const PricingPlansTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Pricing Plans</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => showPreview()}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700"
          >
            <Eye size={18} /> Preview
          </button>
          <button
            onClick={() => setShowNewPlanForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} /> New Plan
          </button>
        </div>
      </div>

      {showNewPlanForm && (
        <PricingPlanForm
          onSubmit={handleCreatePlan}
          onCancel={() => setShowNewPlanForm(false)}
        />
      )}

      <div className="space-y-4">
        {pricingPlans.map((plan) => (
          <div
            key={plan._id}
            className={`rounded-2xl p-5 transition shadow-sm border ${plan.isPrimary ? "bg-gradient-to-br from-violet-600 to-purple-700 text-white border-violet-500" : "bg-white border-slate-200 hover:shadow-md"}`}
          >
            {editingPlan?._id === plan._id ? (
              <PricingPlanEditForm
                plan={editingPlan}
                onSave={(updatedPlan) =>
                  handleUpdatePlan(plan._id, updatedPlan)
                }
                onPreview={(updatedPlan) =>
                  showPreview({
                    plans: pricingPlans.map((p) =>
                      p._id === plan._id ? { ...p, ...updatedPlan } : p,
                    ),
                  })
                }
                onCancel={() => setEditingPlan(null)}
              />
            ) : (
              <>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    <p
                      className={`text-sm ${plan.isPrimary ? "text-white/80" : "text-gray-600"}`}
                    >
                      {plan.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingPlan(plan)}
                      className={`p-2 rounded ${plan.isPrimary ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => showPreview({ plans: pricingPlans })}
                      className={`p-2 rounded ${plan.isPrimary ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan._id)}
                      className={`p-2 rounded ${plan.isPrimary ? "hover:bg-white/10" : "hover:bg-red-100"}`}
                    >
                      <Trash2
                        size={18}
                        className={
                          plan.isPrimary ? "text-white" : "text-red-600"
                        }
                      />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span className="font-semibold">Monthly:</span> ₹
                    {plan.monthlyPrice}
                  </div>
                  <div>
                    <span className="font-semibold">Annual:</span> ₹
                    {plan.annualPrice}
                  </div>
                  <div>
                    <span className="font-semibold">Primary:</span>{" "}
                    {plan.isPrimary ? "✓" : "✗"}
                  </div>
                  <div>
                    <span className="font-semibold">Active:</span>{" "}
                    {plan.isActive ? "✓" : "✗"}
                  </div>
                </div>
                <div
                  className={`text-sm ${plan.isPrimary ? "text-white/80" : "text-gray-600"}`}
                >
                  <span className="font-semibold">Features:</span>{" "}
                  {plan.features?.length || 0}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const FAQsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">FAQs</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => showPreview()}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700"
          >
            <Eye size={18} /> Preview
          </button>
          <button
            onClick={() => setShowNewFAQForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} /> New FAQ
          </button>
        </div>
      </div>

      {showNewFAQForm && (
        <FAQForm
          onSubmit={handleCreateFAQ}
          onCancel={() => setShowNewFAQForm(false)}
        />
      )}

      <div className="space-y-4">
        {faqs.map((faq) => (
          <div
            key={faq._id}
            className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition"
          >
            {editingFAQ?._id === faq._id ? (
              <FAQEditForm
                faq={editingFAQ}
                onSave={(updatedFaq) => handleUpdateFAQ(faq._id, updatedFaq)}
                onPreview={(updatedFaq) =>
                  showPreview({
                    nextFaqs: faqs.map((f) =>
                      f._id === faq._id ? { ...f, ...updatedFaq } : f,
                    ),
                  })
                }
                onCancel={() => setEditingFAQ(null)}
              />
            ) : (
              <>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold mb-2">{faq.question}</h3>
                    <p className="text-sm text-gray-600 mb-2">{faq.answer}</p>
                    <div className="flex gap-4 text-xs">
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        {faq.category}
                      </span>
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        Order: {faq.displayOrder}
                      </span>
                      {faq.isActive ? (
                        <span className="text-green-600">Active</span>
                      ) : (
                        <span className="text-red-600">Inactive</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingFAQ(faq)}
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => showPreview({ nextFaqs: faqs })}
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteFAQ(faq._id)}
                      className="p-2 hover:bg-red-100 rounded"
                    >
                      <Trash2 size={18} className="text-red-600" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const SettingsTab = () => (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Pricing Page Settings</h2>
        <button
          onClick={() => showPreview()}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700"
        >
          <Eye size={18} /> Preview
        </button>
      </div>

      {settings && (
        <PricingSettingsForm
          settings={settings}
          onSave={handleUpdateSettings}
          onPreview={(draftSettings) =>
            showPreview({ nextSettings: draftSettings })
          }
        />
      )}
    </div>
  );

  return (
    <div
      className={`p-6 min-h-screen rounded-lg ${isDarkTheme ? "bg-slate-800 text-slate-100" : "bg-gray-50"}`}
    >
      <h1
        className={`text-3xl font-bold mb-6 ${isDarkTheme ? "text-slate-100" : "text-gray-900"}`}
      >
        Pricing Management
      </h1>

      {/* Tabs */}
      <div
        className={`flex gap-4 mb-6 border-b ${isDarkTheme ? "border-slate-700" : ""}`}
      >
        {["plans", "faqs", "settings"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-semibold capitalize border-b-2 transition ${
              activeTab === tab
                ? `${isDarkTheme ? "border-blue-400 text-blue-400" : "border-blue-600 text-blue-600"}`
                : `${isDarkTheme ? "border-transparent text-slate-400 hover:text-blue-400" : "border-transparent text-gray-600 hover:text-blue-600"}`
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div
          className={`text-center py-8 ${isDarkTheme ? "text-slate-400" : ""}`}
        >
          Loading...
        </div>
      ) : (
        <div
          className={`${previewOpen ? "grid grid-cols-1 xl:grid-cols-[1fr_1.2fr] gap-6" : ""}`}
        >
          <div>
            {activeTab === "plans" && <PricingPlansTab />}
            {activeTab === "faqs" && <FAQsTab />}
            {activeTab === "settings" && <SettingsTab />}
          </div>
          {previewOpen && previewData && (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">Client Preview</h3>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="p-2 rounded hover:bg-slate-100"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="max-h-[78vh] overflow-auto">
                <PricingPage previewData={previewData} embedded />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── HELPER COMPONENTS ───

const PricingPlanForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    monthlyPrice: "",
    annualPrice: "",
    monthlyPriceUSD: "",
    annualPriceUSD: "",
    buttonText: "Start Free Trial",
    buttonLink: "/contact",
    isPrimary: false,
    isActive: true,
    featuresTitle: "Includes:",
    features: [{ text: "" }],
    options: [""],
    defaultOption: "",
    displayOrder: 0,
  });

  const normalizePayload = () => ({
    ...formData,
    monthlyPrice:
      formData.monthlyPrice === "" ? null : Number(formData.monthlyPrice),
    annualPrice:
      formData.annualPrice === "" ? null : Number(formData.annualPrice),
    monthlyPriceUSD:
      formData.monthlyPriceUSD === "" ? null : Number(formData.monthlyPriceUSD),
    annualPriceUSD:
      formData.annualPriceUSD === "" ? null : Number(formData.annualPriceUSD),
    displayOrder:
      formData.displayOrder === "" || formData.displayOrder === null
        ? 0
        : Number(formData.displayOrder),
    features: (formData.features || [])
      .map((feature) => ({ text: String(feature.text || "").trim() }))
      .filter((feature) => feature.text),
    options: (formData.options || [])
      .map((option) => option.trim())
      .filter(Boolean),
    defaultOption: formData.defaultOption || null,
  });

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-gradient-to-b from-white to-slate-50/60 shadow-sm">
      <h3 className="text-lg font-bold mb-4 text-slate-900">
        Create New Pricing Plan
      </h3>

      <div className="space-y-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Plan name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
          <input
            type="text"
            placeholder="Features title"
            value={formData.featuresTitle}
            onChange={(e) =>
              setFormData({ ...formData, featuresTitle: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
        </div>

        <textarea
          placeholder="Plan description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5 min-h-[90px]"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="number"
            placeholder="Monthly price (INR)"
            value={formData.monthlyPrice}
            onChange={(e) =>
              setFormData({ ...formData, monthlyPrice: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
          <input
            type="number"
            placeholder="Annual price (INR)"
            value={formData.annualPrice}
            onChange={(e) =>
              setFormData({ ...formData, annualPrice: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
          <input
            type="number"
            placeholder="Monthly price (USD)"
            value={formData.monthlyPriceUSD}
            onChange={(e) =>
              setFormData({ ...formData, monthlyPriceUSD: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
          <input
            type="number"
            placeholder="Annual price (USD)"
            value={formData.annualPriceUSD}
            onChange={(e) =>
              setFormData({ ...formData, annualPriceUSD: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            value={formData.buttonText}
            onChange={(e) =>
              setFormData({ ...formData, buttonText: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Button text"
          />
          <input
            type="text"
            value={formData.buttonLink}
            onChange={(e) =>
              setFormData({ ...formData, buttonLink: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Button link"
          />
          <input
            type="text"
            value={formData.defaultOption || ""}
            onChange={(e) =>
              setFormData({ ...formData, defaultOption: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Default option"
          />
          <input
            type="number"
            value={formData.displayOrder ?? ""}
            onChange={(e) =>
              setFormData({ ...formData, displayOrder: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Display order"
          />
        </div>

        <div className="rounded-xl border border-slate-200 p-3 bg-white">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800">Features</p>
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  features: [...formData.features, { text: "" }],
                })
              }
              className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
            >
              Add Feature
            </button>
          </div>
          <div className="space-y-2">
            {formData.features.map((feature, index) => (
              <div key={`new-feature-${index}`} className="flex gap-2">
                <input
                  type="text"
                  value={feature.text || ""}
                  onChange={(e) => {
                    const nextFeatures = [...formData.features];
                    nextFeatures[index] = { ...feature, text: e.target.value };
                    setFormData({ ...formData, features: nextFeatures });
                  }}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder={`Feature ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (formData.features.length === 1) {
                      setFormData({ ...formData, features: [{ text: "" }] });
                      return;
                    }
                    setFormData({
                      ...formData,
                      features: formData.features.filter((_, i) => i !== index),
                    });
                  }}
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-3 bg-white">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800">Plan Options</p>
            <button
              type="button"
              onClick={() =>
                setFormData({ ...formData, options: [...formData.options, ""] })
              }
              className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
            >
              Add Option
            </button>
          </div>
          <div className="space-y-2">
            {formData.options.map((option, index) => (
              <div key={`new-option-${index}`} className="flex gap-2">
                <input
                  type="text"
                  value={option || ""}
                  onChange={(e) => {
                    const nextOptions = [...formData.options];
                    nextOptions[index] = e.target.value;
                    setFormData({ ...formData, options: nextOptions });
                  }}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder={`Option ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (formData.options.length === 1) {
                      setFormData({ ...formData, options: [""] });
                      return;
                    }
                    setFormData({
                      ...formData,
                      options: formData.options.filter((_, i) => i !== index),
                    });
                  }}
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) =>
                setFormData({ ...formData, isPrimary: e.target.checked })
              }
            />
            Mark as Primary
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={formData.isActive !== false}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
            />
            Active
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => onSubmit(normalizePayload())}
          className="bg-blue-600 text-white px-5 py-2 rounded-xl hover:bg-blue-700"
        >
          Create
        </button>
        <button
          onClick={onCancel}
          className="bg-slate-200 px-5 py-2 rounded-xl hover:bg-slate-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const PricingPlanEditForm = ({ plan, onSave, onPreview, onCancel }) => {
  const [draft, setDraft] = useState(() => ({
    ...plan,
    featuresDraft: (plan.features || []).map((feature) => ({
      ...feature,
      text: feature.text || "",
    })),
    optionsDraft: (plan.options || []).length
      ? [...(plan.options || [])]
      : [""],
  }));

  useEffect(() => {
    setDraft({
      ...plan,
      featuresDraft: (plan.features || []).map((feature) => ({
        ...feature,
        text: feature.text || "",
      })),
      optionsDraft: (plan.options || []).length
        ? [...(plan.options || [])]
        : [""],
    });
  }, [plan]);

  const normalizePayload = () => {
    return {
      ...draft,
      monthlyPrice:
        draft.monthlyPrice === "" || draft.monthlyPrice === null
          ? null
          : Number(draft.monthlyPrice),
      annualPrice:
        draft.annualPrice === "" || draft.annualPrice === null
          ? null
          : Number(draft.annualPrice),
      monthlyPriceUSD:
        draft.monthlyPriceUSD === "" || draft.monthlyPriceUSD === null
          ? null
          : Number(draft.monthlyPriceUSD),
      annualPriceUSD:
        draft.annualPriceUSD === "" || draft.annualPriceUSD === null
          ? null
          : Number(draft.annualPriceUSD),
      displayOrder:
        draft.displayOrder === "" || draft.displayOrder === null
          ? 0
          : Number(draft.displayOrder),
      features: (draft.featuresDraft || [])
        .map((feature) => ({
          ...feature,
          text: String(feature.text || "").trim(),
          hasInfo: Boolean(feature.hasInfo),
        }))
        .filter((feature) => feature.text),
      options: (draft.optionsDraft || [])
        .map((option) => option.trim())
        .filter(Boolean),
      defaultOption: draft.defaultOption || null,
      featuresDraft: undefined,
      optionsDraft: undefined,
    };
  };

  return (
    <div className="space-y-4 mb-4 pb-4 border-b border-slate-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={draft.name || ""}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Plan name"
        />
        <input
          type="text"
          value={draft.featuresTitle || ""}
          onChange={(e) =>
            setDraft({ ...draft, featuresTitle: e.target.value })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Features title"
        />
      </div>

      <textarea
        value={draft.description || ""}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        className="w-full border border-slate-300 rounded-xl px-3 py-2.5 min-h-[90px]"
        placeholder="Plan description"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="number"
          value={draft.monthlyPrice ?? ""}
          onChange={(e) => setDraft({ ...draft, monthlyPrice: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Monthly price"
        />
        <input
          type="number"
          value={draft.annualPrice ?? ""}
          onChange={(e) => setDraft({ ...draft, annualPrice: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Annual price"
        />
        <input
          type="number"
          value={draft.monthlyPriceUSD ?? ""}
          onChange={(e) =>
            setDraft({ ...draft, monthlyPriceUSD: e.target.value })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Monthly price USD"
        />
        <input
          type="number"
          value={draft.annualPriceUSD ?? ""}
          onChange={(e) =>
            setDraft({ ...draft, annualPriceUSD: e.target.value })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Annual price USD"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={draft.buttonText || ""}
          onChange={(e) => setDraft({ ...draft, buttonText: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Button text"
        />
        <input
          type="text"
          value={draft.buttonLink || ""}
          onChange={(e) => setDraft({ ...draft, buttonLink: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Button link"
        />
        <input
          type="text"
          value={draft.defaultOption || ""}
          onChange={(e) =>
            setDraft({ ...draft, defaultOption: e.target.value })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Default option"
        />
        <input
          type="number"
          value={draft.displayOrder ?? ""}
          onChange={(e) => setDraft({ ...draft, displayOrder: e.target.value })}
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Display order"
        />
      </div>

      <input
        type="text"
        value={draft.priceLabel || ""}
        onChange={(e) => setDraft({ ...draft, priceLabel: e.target.value })}
        className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
        placeholder="Price label (for custom plans)"
      />

      <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-800">Features</p>
          <button
            type="button"
            onClick={() =>
              setDraft({
                ...draft,
                featuresDraft: [
                  ...(draft.featuresDraft || []),
                  { text: "", hasInfo: false },
                ],
              })
            }
            className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
          >
            Add Feature
          </button>
        </div>
        <div className="space-y-2">
          {(draft.featuresDraft || []).length === 0 && (
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  featuresDraft: [{ text: "", hasInfo: false }],
                })
              }
              className="text-sm px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Add first feature
            </button>
          )}
          {(draft.featuresDraft || []).map((feature, index) => (
            <div
              key={`feature-${index}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center"
            >
              <input
                type="text"
                value={feature.text || ""}
                onChange={(e) => {
                  const nextFeatures = [...(draft.featuresDraft || [])];
                  nextFeatures[index] = { ...feature, text: e.target.value };
                  setDraft({ ...draft, featuresDraft: nextFeatures });
                }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder={`Feature ${index + 1}`}
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(feature.hasInfo)}
                  onChange={(e) => {
                    const nextFeatures = [...(draft.featuresDraft || [])];
                    nextFeatures[index] = {
                      ...feature,
                      hasInfo: e.target.checked,
                    };
                    setDraft({ ...draft, featuresDraft: nextFeatures });
                  }}
                />
                Info icon
              </label>
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    featuresDraft: (draft.featuresDraft || []).filter(
                      (_, i) => i !== index,
                    ),
                  })
                }
                className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-800">Options</p>
          <button
            type="button"
            onClick={() =>
              setDraft({
                ...draft,
                optionsDraft: [...(draft.optionsDraft || []), ""],
              })
            }
            className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
          >
            Add Option
          </button>
        </div>
        <div className="space-y-2">
          {(draft.optionsDraft || []).map((option, index) => (
            <div key={`option-${index}`} className="flex gap-2">
              <input
                type="text"
                value={option || ""}
                onChange={(e) => {
                  const nextOptions = [...(draft.optionsDraft || [])];
                  nextOptions[index] = e.target.value;
                  setDraft({ ...draft, optionsDraft: nextOptions });
                }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder={`Option ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => {
                  const remaining = (draft.optionsDraft || []).filter(
                    (_, i) => i !== index,
                  );
                  setDraft({
                    ...draft,
                    optionsDraft: remaining.length ? remaining : [""],
                  });
                }}
                className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(draft.isPrimary)}
            onChange={(e) =>
              setDraft({ ...draft, isPrimary: e.target.checked })
            }
          />
          <span>Primary plan</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.isActive !== false}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          <span>Active</span>
        </label>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onPreview(normalizePayload())}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700"
        >
          Preview
        </button>
        <button
          onClick={() => onSave(normalizePayload())}
          className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="bg-slate-200 px-4 py-2 rounded-xl hover:bg-slate-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const FAQForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "general",
    isActive: true,
  });

  return (
    <div className="border rounded-lg p-4 bg-white">
      <h3 className="text-lg font-bold mb-4">Create New FAQ</h3>
      <div className="space-y-3 mb-4">
        <input
          type="text"
          placeholder="Question"
          value={formData.question}
          onChange={(e) =>
            setFormData({ ...formData, question: e.target.value })
          }
          className="w-full border rounded px-3 py-2"
        />
        <textarea
          placeholder="Answer"
          value={formData.answer}
          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
          className="w-full border rounded px-3 py-2 h-24"
        />
        <select
          value={formData.category}
          onChange={(e) =>
            setFormData({ ...formData, category: e.target.value })
          }
          className="w-full border rounded px-3 py-2"
        >
          <option value="general">General</option>
          <option value="billing">Billing</option>
          <option value="technical">Technical</option>
          <option value="support">Support</option>
        </select>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData({ ...formData, isActive: e.target.checked })
            }
          />
          <span>Active</span>
        </label>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => onSubmit(formData)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Create
        </button>
        <button
          onClick={onCancel}
          className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const FAQEditForm = ({ faq, onSave, onPreview, onCancel }) => {
  const [draft, setDraft] = useState({ ...faq });

  useEffect(() => {
    setDraft({ ...faq });
  }, [faq]);

  return (
    <div className="space-y-3 mb-4 pb-4 border-b border-slate-200">
      <input
        type="text"
        value={draft.question || ""}
        onChange={(e) => setDraft({ ...draft, question: e.target.value })}
        className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
      />
      <textarea
        value={draft.answer || ""}
        onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
        className="w-full border border-slate-300 rounded-xl px-3 py-2.5 h-24"
      />
      <select
        value={draft.category || "general"}
        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
        className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
      >
        <option value="general">General</option>
        <option value="billing">Billing</option>
        <option value="technical">Technical</option>
        <option value="support">Support</option>
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="number"
          value={draft.displayOrder ?? ""}
          onChange={(e) =>
            setDraft({
              ...draft,
              displayOrder: e.target.value === "" ? "" : Number(e.target.value),
            })
          }
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
          placeholder="Display order"
        />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.isActive !== false}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          <span>Active</span>
        </label>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onPreview(draft)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700"
        >
          Preview
        </button>
        <button
          onClick={() => onSave(draft)}
          className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="bg-slate-200 px-4 py-2 rounded-xl hover:bg-slate-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const PricingSettingsForm = ({ settings, onSave, onPreview }) => {
  const [draft, setDraft] = useState(() => ({
    ...settings,
    footerEligibilityDraft: (settings.footerEligibility || []).length
      ? [...settings.footerEligibility]
      : [""],
    footerStepsDraft: (settings.footerSteps || []).length
      ? [...settings.footerSteps]
      : [""],
    clientLogosDraft: (settings.clientLogos || []).length
      ? [...settings.clientLogos]
      : [""],
    trustBadgesDraft: (settings.trustBadges || []).length
      ? settings.trustBadges.map((badge) => ({
          label: badge.label || "",
          icon: badge.icon || "",
        }))
      : [{ label: "", icon: "" }],
  }));

  useEffect(() => {
    setDraft({
      ...settings,
      footerEligibilityDraft: (settings.footerEligibility || []).length
        ? [...settings.footerEligibility]
        : [""],
      footerStepsDraft: (settings.footerSteps || []).length
        ? [...settings.footerSteps]
        : [""],
      clientLogosDraft: (settings.clientLogos || []).length
        ? [...settings.clientLogos]
        : [""],
      trustBadgesDraft: (settings.trustBadges || []).length
        ? settings.trustBadges.map((badge) => ({
            label: badge.label || "",
            icon: badge.icon || "",
          }))
        : [{ label: "", icon: "" }],
    });
  }, [settings]);

  const buildPayload = () => ({
    ...draft,
    toggleSavePercentage:
      draft.toggleSavePercentage === "" || draft.toggleSavePercentage === null
        ? 0
        : Number(draft.toggleSavePercentage),
    conversionRate:
      draft.conversionRate === "" || draft.conversionRate === null
        ? 0
        : Number(draft.conversionRate),
    footerEligibility: (draft.footerEligibilityDraft || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
    footerSteps: (draft.footerStepsDraft || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
    clientLogos: (draft.clientLogosDraft || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
    trustBadges: (draft.trustBadgesDraft || [])
      .map((badge) => ({
        label: String(badge.label || "").trim(),
        icon: String(badge.icon || "").trim(),
      }))
      .filter((badge) => badge.label && badge.icon),
    footerEligibilityDraft: undefined,
    footerStepsDraft: undefined,
    clientLogosDraft: undefined,
    trustBadgesDraft: undefined,
  });

  return (
    <div className="space-y-6 border border-slate-200 rounded-2xl p-6 bg-gradient-to-b from-white to-slate-50/60 shadow-sm">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-bold mb-3">Hero</h3>
        <div className="space-y-3">
          <input
            type="text"
            value={draft.heroTitle || ""}
            onChange={(e) => setDraft({ ...draft, heroTitle: e.target.value })}
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Hero title"
          />
          <input
            type="text"
            value={draft.heroSubtitle || ""}
            onChange={(e) =>
              setDraft({ ...draft, heroSubtitle: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Hero subtitle"
          />
          <textarea
            value={draft.heroDescription || ""}
            onChange={(e) =>
              setDraft({ ...draft, heroDescription: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5 h-24"
            placeholder="Hero description"
          />
          <input
            type="text"
            value={draft.heroImageUrl || ""}
            onChange={(e) =>
              setDraft({ ...draft, heroImageUrl: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Hero image URL"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-bold mb-3">Footer & CTA</h3>
        <div className="space-y-3">
          <input
            type="text"
            value={draft.footerTitle || ""}
            onChange={(e) =>
              setDraft({ ...draft, footerTitle: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Footer title"
          />
          <textarea
            value={draft.footerDescription || ""}
            onChange={(e) =>
              setDraft({ ...draft, footerDescription: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5 h-20"
            placeholder="Footer description"
          />
          <input
            type="text"
            value={draft.footerButtonText || ""}
            onChange={(e) =>
              setDraft({ ...draft, footerButtonText: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Footer button text"
          />
          <input
            type="text"
            value={draft.footerButtonLink || ""}
            onChange={(e) =>
              setDraft({ ...draft, footerButtonLink: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Footer button link"
          />

          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">
                Footer Eligibility
              </p>
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    footerEligibilityDraft: [
                      ...(draft.footerEligibilityDraft || []),
                      "",
                    ],
                  })
                }
                className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
              >
                Add Point
              </button>
            </div>
            <div className="space-y-2">
              {(draft.footerEligibilityDraft || []).map((item, index) => (
                <div key={`eligibility-${index}`} className="flex gap-2">
                  <input
                    type="text"
                    value={item || ""}
                    onChange={(e) => {
                      const next = [...(draft.footerEligibilityDraft || [])];
                      next[index] = e.target.value;
                      setDraft({ ...draft, footerEligibilityDraft: next });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    placeholder={`Eligibility point ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (draft.footerEligibilityDraft || []).filter(
                        (_, i) => i !== index,
                      );
                      setDraft({
                        ...draft,
                        footerEligibilityDraft: next.length ? next : [""],
                      });
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">
                How To Claim Steps
              </p>
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    footerStepsDraft: [...(draft.footerStepsDraft || []), ""],
                  })
                }
                className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
              >
                Add Step
              </button>
            </div>
            <div className="space-y-2">
              {(draft.footerStepsDraft || []).map((item, index) => (
                <div key={`step-${index}`} className="flex gap-2">
                  <input
                    type="text"
                    value={item || ""}
                    onChange={(e) => {
                      const next = [...(draft.footerStepsDraft || [])];
                      next[index] = e.target.value;
                      setDraft({ ...draft, footerStepsDraft: next });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    placeholder={`Step ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (draft.footerStepsDraft || []).filter(
                        (_, i) => i !== index,
                      );
                      setDraft({
                        ...draft,
                        footerStepsDraft: next.length ? next : [""],
                      });
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <input
            type="text"
            value={draft.ctaTitle || ""}
            onChange={(e) => setDraft({ ...draft, ctaTitle: e.target.value })}
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="CTA title"
          />
          <textarea
            value={draft.ctaDescription || ""}
            onChange={(e) =>
              setDraft({ ...draft, ctaDescription: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5 h-20"
            placeholder="CTA description"
          />
          <input
            type="text"
            value={draft.ctaButtonText || ""}
            onChange={(e) =>
              setDraft({ ...draft, ctaButtonText: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="CTA button text"
          />
          <input
            type="text"
            value={draft.ctaButtonLink || ""}
            onChange={(e) =>
              setDraft({ ...draft, ctaButtonLink: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="CTA button link"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-lg font-bold mb-3">Display & Trust</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(draft.isAnnualToggleEnabled)}
              onChange={(e) =>
                setDraft({ ...draft, isAnnualToggleEnabled: e.target.checked })
              }
            />
            <span>Enable annual/monthly toggle</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              min="0"
              max="100"
              value={draft.toggleSavePercentage ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, toggleSavePercentage: e.target.value })
              }
              className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
              placeholder="Discount %"
            />
            <input
              type="number"
              step="0.1"
              value={draft.conversionRate ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, conversionRate: e.target.value })
              }
              className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
              placeholder="Conversion rate"
            />
          </div>
          <input
            type="text"
            value={draft.discountCopy || ""}
            onChange={(e) =>
              setDraft({ ...draft, discountCopy: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Discount copy"
          />
          <input
            type="text"
            value={draft.footerNote || ""}
            onChange={(e) => setDraft({ ...draft, footerNote: e.target.value })}
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Footer note"
          />
          <input
            type="text"
            value={draft.clientLogosLabel || ""}
            onChange={(e) =>
              setDraft({ ...draft, clientLogosLabel: e.target.value })
            }
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5"
            placeholder="Client logos label"
          />

          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">
                Client Logos
              </p>
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    clientLogosDraft: [...(draft.clientLogosDraft || []), ""],
                  })
                }
                className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
              >
                Add Logo URL
              </button>
            </div>
            <div className="space-y-2">
              {(draft.clientLogosDraft || []).map((logo, index) => (
                <div key={`logo-${index}`} className="flex gap-2">
                  <input
                    type="text"
                    value={logo || ""}
                    onChange={(e) => {
                      const next = [...(draft.clientLogosDraft || [])];
                      next[index] = e.target.value;
                      setDraft({ ...draft, clientLogosDraft: next });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    placeholder={`Logo URL ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (draft.clientLogosDraft || []).filter(
                        (_, i) => i !== index,
                      );
                      setDraft({
                        ...draft,
                        clientLogosDraft: next.length ? next : [""],
                      });
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">
                Trust Badges
              </p>
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    trustBadgesDraft: [
                      ...(draft.trustBadgesDraft || []),
                      { label: "", icon: "" },
                    ],
                  })
                }
                className="text-sm px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
              >
                Add Badge
              </button>
            </div>
            <div className="space-y-2">
              {(draft.trustBadgesDraft || []).map((badge, index) => (
                <div
                  key={`badge-${index}`}
                  className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center"
                >
                  <input
                    type="text"
                    value={badge.label || ""}
                    onChange={(e) => {
                      const next = [...(draft.trustBadgesDraft || [])];
                      next[index] = { ...badge, label: e.target.value };
                      setDraft({ ...draft, trustBadgesDraft: next });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    placeholder="Badge label"
                  />
                  <input
                    type="text"
                    value={badge.icon || ""}
                    onChange={(e) => {
                      const next = [...(draft.trustBadgesDraft || [])];
                      next[index] = { ...badge, icon: e.target.value };
                      setDraft({ ...draft, trustBadgesDraft: next });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    placeholder="Badge icon"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (draft.trustBadgesDraft || []).filter(
                        (_, i) => i !== index,
                      );
                      setDraft({
                        ...draft,
                        trustBadgesDraft: next.length
                          ? next
                          : [{ label: "", icon: "" }],
                      });
                    }}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => onPreview(buildPayload())}
          className="bg-indigo-600 text-white px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-indigo-700"
        >
          <Eye size={20} /> Preview Settings
        </button>

        <button
          onClick={() => onSave(buildPayload())}
          className="bg-blue-600 text-white px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-blue-700"
        >
          <Save size={20} /> Save Settings
        </button>
      </div>
    </div>
  );
};

export default PricingAdminDashboard;
